import { useState } from 'react';
import { ArrowLeft, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import type { QueryResult, VisualizationType } from '../../types/analytics.types';
import { ChartRenderer } from './ChartRenderer';
import { CardMenu } from './CardMenu';
import { useAnalyticsStore } from '../../stores/analyticsStore';
import { usePatientDrilldown } from '../../hooks/usePatientDrilldown';
import { formatDate } from '../../lib/formatters';

interface ResultsCardProps {
  result: QueryResult;
  visualization: VisualizationType;
  onSaveToDashboard: () => void;
}

export function ResultsCard({
  result,
  visualization,
  onSaveToDashboard,
}: ResultsCardProps) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [title, setTitle] = useState(result.query.title);

  const { setSelectedVisualization, reset } = useAnalyticsStore();
  const patientDrilldown = usePatientDrilldown();

  const handleBack = () => {
    reset();
  };

  const handleChangeVisualization = (type: VisualizationType) => {
    setSelectedVisualization(type);
  };

  const handleViewList = () => {
    patientDrilldown.mutate({});
  };

  const handleExport = () => {
    console.log('Export data');
  };

  const handleTitleSubmit = () => {
    setIsEditingTitle(false);
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>

          {isEditingTitle ? (
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={handleTitleSubmit}
              onKeyDown={(e) => e.key === 'Enter' && handleTitleSubmit()}
              className="h-8 w-full sm:w-[200px]"
              autoFocus
            />
          ) : (
            <CardTitle
              className="text-lg cursor-pointer hover:text-muted-foreground break-words"
              onClick={() => setIsEditingTitle(true)}
            >
              {title}
            </CardTitle>
          )}
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            {result.meta.executionTimeMs}ms
          </div>
          <CardMenu
            resultType={result.data.type}
            currentVisualization={visualization}
            onChangeVisualization={handleChangeVisualization}
            onViewList={handleViewList}
            onSaveToDashboard={onSaveToDashboard}
            onExport={handleExport}
          />
        </div>
      </CardHeader>

      <CardContent className="min-w-0">
        <ChartRenderer result={result} visualizationType={visualization} />

        <div className="flex flex-wrap items-center justify-between gap-2 pt-4 mt-4 border-t text-xs text-muted-foreground">
          <span>
            Total: {result.meta.totalCount.toLocaleString()} patients
          </span>
          <span>
            Generated {formatDate(result.meta.generatedAt)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
